export type AppIconName =
  | 'home'
  | 'wallet'
  | 'chart'
  | 'repeat'
  | 'settings'
  | 'bell'
  | 'spark'
  | 'receipt'
  | 'trash'
  | 'sliders'
  | 'inbox'
  | 'check'
  | 'user'
  | 'refresh'
  | 'share'
  | 'copy'
  | 'plus'
  | 'link'
  | 'chevronDown'
  | 'chevronUp'
  | 'edit'
  | 'crown'
  | 'history'
  | 'warning'
  | 'users';

interface AppIconProps {
  name: AppIconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
}

export function AppIcon({ name, size = 20, className, strokeWidth = 1.8 }: AppIconProps) {
  const commonProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
  };

  switch (name) {
    case 'home':
      return <svg {...commonProps}><path d="m3.5 10.8 8.5-7 8.5 7" /><path d="M5.5 9.8v9.5h13V9.8" /><path d="M9.5 19.3v-5.4h5v5.4" /></svg>;
    case 'wallet':
      return <svg {...commonProps}><path d="M4.5 6.5h13a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" /><path d="M4.5 6.5V5a2 2 0 0 1 2-2h9.8" /><path d="M19.5 11h-4a2 2 0 0 0 0 4h4" /><path d="M16 13h.01" /></svg>;
    case 'chart':
      return <svg {...commonProps}><path d="M4 19V5" /><path d="M4 19h16" /><path d="m7 15 3-3 2.2 1.8L17.5 8" /><path d="M15 8h2.5v2.5" /></svg>;
    case 'repeat':
      return <svg {...commonProps}><path d="M17 3.5 20.5 7 17 10.5" /><path d="M3.5 7h17" /><path d="m7 20.5-3.5-3.5L7 13.5" /><path d="M20.5 17h-17" /></svg>;
    case 'settings':
      return <svg {...commonProps}><circle cx="12" cy="12" r="3.5" /><path d="m19.4 13.5 1.1.8-1.8 3.1-1.3-.5a7.4 7.4 0 0 1-1.8 1l-.2 1.4h-3.6l-.2-1.4a7.4 7.4 0 0 1-1.8-1l-1.3.5-1.8-3.1 1.1-.8a7.5 7.5 0 0 1 0-2.9l-1.1-.8 1.8-3.1 1.3.5a7.4 7.4 0 0 1 1.8-1l.2-1.4h3.6l.2 1.4a7.4 7.4 0 0 1 1.8 1l1.3-.5 1.8 3.1-1.1.8a7.5 7.5 0 0 1 0 2.9Z" /></svg>;
    case 'bell':
      return <svg {...commonProps}><path d="M18 9.8a6 6 0 0 0-12 0c0 7-2.5 7-2.5 8.5h17C20.5 16.8 18 16.8 18 9.8Z" /><path d="M9.5 21h5" /></svg>;
    case 'spark':
      return <svg {...commonProps}><path d="m12 3 1.35 5.65L19 10l-5.65 1.35L12 17l-1.35-5.65L5 10l5.65-1.35L12 3Z" /><path d="m19 15 .6 2.4L22 18l-2.4.6L19 21l-.6-2.4L16 18l2.4-.6L19 15Z" /></svg>;
    case 'receipt':
      return <svg {...commonProps}><path d="M6 3.5h12v17l-2.3-1.5-2.3 1.5-2.3-1.5-2.3 1.5L6 19V3.5Z" /><path d="M9 8h6M9 12h6M9 16h3.5" /></svg>;
    case 'trash':
      return <svg {...commonProps}><path d="M4 7h16M9 7V4h6v3M7 7l1 13h8l1-13M10 11v5M14 11v5" /></svg>;
    case 'sliders':
      return <svg {...commonProps}><path d="M4 7h5M15 7h5M12 4v6M4 17h9M19 17h1M16 14v6" /></svg>;
    case 'inbox':
      return <svg {...commonProps}><path d="M4.5 5.5h15l2 9v4h-19v-4l2-9Z" /><path d="M2.5 14.5h5l1.5 2h6l1.5-2h5" /></svg>;
    case 'check':
      return <svg {...commonProps}><path d="m5 12.5 4.3 4.3L19 7" /></svg>;
    case 'user':
      return <svg {...commonProps}><circle cx="12" cy="8" r="3.5" /><path d="M5 20c.7-4 3-6 7-6s6.3 2 7 6" /></svg>;
    case 'refresh':
      return <svg {...commonProps}><path d="M20 7v5h-5" /><path d="M4 17v-5h5" /><path d="M18.2 10A7 7 0 0 0 6.5 6.5L4 9M5.8 14A7 7 0 0 0 17.5 17.5L20 15" /></svg>;
    case 'share':
      return <svg {...commonProps}><circle cx="18" cy="5" r="2.3" /><circle cx="6" cy="12" r="2.3" /><circle cx="18" cy="19" r="2.3" /><path d="m8 10.8 8-4.6M8 13.2l8 4.6" /></svg>;
    case 'copy':
      return <svg {...commonProps}><rect x="8" y="8" width="11" height="11" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></svg>;
    case 'plus':
      return <svg {...commonProps}><path d="M12 5v14M5 12h14" /></svg>;
    case 'link':
      return <svg {...commonProps}><path d="m9.5 14.5 5-5" /><path d="M7.2 17.8 5.7 19.3a3.5 3.5 0 0 1-5-5l3-3a3.5 3.5 0 0 1 5 0" transform="translate(2)" /><path d="m14.8 6.2 1.5-1.5a3.5 3.5 0 0 1 5 5l-3 3a3.5 3.5 0 0 1-5 0" transform="translate(-2)" /></svg>;
    case 'chevronDown':
      return <svg {...commonProps}><path d="m6 9 6 6 6-6" /></svg>;
    case 'chevronUp':
      return <svg {...commonProps}><path d="m6 15 6-6 6 6" /></svg>;
    case 'edit':
      return <svg {...commonProps}><path d="m14 5 5 5L9 20H4v-5L14 5Z" /><path d="m12 7 5 5" /></svg>;
    case 'crown':
      return <svg {...commonProps}><path d="m4 8 4 4 4-7 4 7 4-4-1.5 10h-13L4 8Z" /><path d="M6 21h12" /></svg>;
    case 'history':
      return <svg {...commonProps}><path d="M4 5v5h5" /><path d="M5.8 17.2A8 8 0 1 0 4 10" /><path d="M12 8v4l3 2" /></svg>;
    case 'warning':
      return <svg {...commonProps}><path d="M10.3 4.2 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></svg>;
    case 'users':
      return <svg {...commonProps}><circle cx="9" cy="8" r="3" /><path d="M3.5 20c.5-4 2.3-6 5.5-6s5 2 5.5 6" /><path d="M16 5.5a3 3 0 0 1 0 5.5M16.5 14c2.4.5 3.7 2.5 4 6" /></svg>;
  }
}
